import React, { useEffect, useState } from "react";
import i18n from "../../i18n/config";
import AllocationsApi from "../../api/allocations";
import { ACTION_DISPLAY_TYPES, BANK_ACCOUNT_STATUS } from "../../constants/enums";
import { AllocationType } from "../../types/api/allocations";
import { useHistory } from "react-router-dom";
import useErrorModal from "../../hooks/useErrorModal";
import { ResetAllocations, SetAvailableAllocations } from "../../actions/allocations";
import { useDispatch } from "react-redux";
import { ROUTES } from "../../constants/clientRoutes";

import FlowScreen from "../../components/Layouts/FlowScreen";
import AllocationTable from "../../components/Allocations/AllocationTable";
import InputSkeletonGroup from "../../components/Common/Loaders/InputSkeleton";
import Modal from "../../components/Common/Modal";
import ApiErrorModal from "../../components/Common/Modals/ApiErrorModal";
import Card from "../../components/Common/Card";
import Action from "../../components/Common/Action";
import RowSkeletonGroup from "../../components/Common/Loaders/RowSkeletonGroup";

const Allocations = () => {
  const [availableAllocations, setAvailableAllocations] = useState<AllocationType[]>();
  const [unavailableAllocations, setUnavailableAllocations] = useState<AllocationType[]>();
  const [showInfoTipModal, setShowInfoTipModal] = useState(false);
  const [infoTipModal, setInfoTipModal] = useState<{ title: string, content: string }>({ title: "", content: "" });
  const [showEditModal, setShowEditModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const openErrorModal = useErrorModal();
  const history = useHistory();
  const dispatch = useDispatch();

  /**
   * Calls getAllocations on component mount
   */
  useEffect(() => {
    dispatch(ResetAllocations());
    getAllocations();
  }, []);

  /**
   * Fetches all allocations and sets them in state
   */
  const getAllocations = async () => {
    try {
      const response = await AllocationsApi.GetAllocations();
      const cardAllocation = formatCardAllocationForTable(response?.cardAllocation);
      const availableBanks = response.bankAllocations.filter((bank: AllocationType) => bank.bankAccountStatus === BANK_ACCOUNT_STATUS.AVAILABLE);

      if (cardAllocation) {
        setAvailableAllocations([cardAllocation, ...availableBanks]);
      } else {
        setAvailableAllocations(availableBanks);
      }

      setUnavailableAllocations(response.bankAllocations.filter((bank: AllocationType) => bank.bankAccountStatus !== BANK_ACCOUNT_STATUS.AVAILABLE));
      dispatch(SetAvailableAllocations([cardAllocation, ...availableBanks]));
      setLoading(false);
    } catch (err) {
      openErrorModal(err);
    }
  }

  /**
   * Formats card allocation for table
   * @param cardAllocation 
   */
  const formatCardAllocationForTable = (cardAllocation?: AllocationType) => {
    if (!cardAllocation) {
      return cardAllocation;
    }

    return {
      ...cardAllocation,
      accountName: `My Card **${cardAllocation.cardNumberLastFour}`,
      name: cardAllocation.accountName,
      service: `${cardAllocation.destinationCurrency.code} to ${cardAllocation.destinationCountry.name}`,
    }
  }

  /**
   * Handles showing info tip modal
   * @param title 
   * @param content 
   */
  const handleInfoTipModal = (title: string, content: string) => {
    setShowInfoTipModal(!showInfoTipModal);
    setInfoTipModal({ title, content });
  }

  const handleOkClick = () => {
    setShowEditModal(false);
    history.push(ROUTES.ALLOCATIONS.ACCOUNT_SELECTION);
  }

  /**
   * Actions for edit allocation modal
   */
  const editAllocationModalActions = [
    {
      title: i18n.t("COMMON.CANCEL"),
      onClick: () => setShowEditModal(false),
      displayType: ACTION_DISPLAY_TYPES.SECONDARY,
    },
    {
      title: i18n.t("COMMON.OK"),
      onClick: () => handleOkClick(),
      displayType: ACTION_DISPLAY_TYPES.PRIMARY
    }
  ];

  return (
    <FlowScreen
      flowTitle={i18n.t("PAGES.ALLOCATIONS.INDEX.TITLE")}
      className="v3 relative"
      showBackButton={true}
      customBackButtonFunc={() => history.push(ROUTES.DASHBOARD)}
      showPrintPage={true}
    >
      <p className="text-secondary mb-24">{i18n.t("PAGES.ALLOCATIONS.INDEX.SUBTITLE_1")}</p>

      {loading &&
        <RowSkeletonGroup numRows={5} />
      }

      {!loading &&
        <>
          {/* Available Bank Accounts */}
          <Card
            fullWidth
            bodyPadding="flush"
            className="mb-16"
            title={i18n.t("PAGES.ALLOCATIONS.INDEX.TABLE.AVAILABLE_HEADING")}
            data-cy="available-allocations-section"
            actions={
              Boolean(availableAllocations?.length) &&
              <Action
                title={i18n.t("PAGES.ALLOCATIONS.INDEX.EDIT_ALLOCATION_BUTTON")}
                displayType={ACTION_DISPLAY_TYPES.PRIMARY}
                size="md"
                width="auto"
                onClick={() => setShowEditModal(true)}
                dataCy="edit-allocation-button"
              />
            }
          >
            <AllocationTable
              isAvailableTableType={true}
              allocations={availableAllocations}
              showStatusCol={true}
              handleInfoTipModal={handleInfoTipModal}
              isIndexPage={true}
            />
          </Card>

          {/* Unavailable Bank Accounts */}
          <Card
            fullWidth
            bodyPadding="flush"
            className="mb-16"
            title={i18n.t("PAGES.ALLOCATIONS.INDEX.TABLE.UNAVAILABLE_HEADING")}
            data-cy="unavailable-allocations-section"
          >
            <AllocationTable
              isAvailableTableType={false}
              allocations={unavailableAllocations}
              showStatusCol={true}
              handleInfoTipModal={handleInfoTipModal}
              isIndexPage={true}
            />
          </Card>

          {/* Edit Allocation Modal */}
          <Modal
            open={showEditModal}
            onClose={() => setShowEditModal(false)}
            title={i18n.t("PAGES.ALLOCATIONS.INDEX.EDIT_ALLOCATION_MODAL.TITLE")}
            content={i18n.t("PAGES.ALLOCATIONS.INDEX.EDIT_ALLOCATION_MODAL.CONTENT")}
            actions={editAllocationModalActions}
          />

          {/* Info tip modal*/}
          <Modal
            onClose={() => setShowInfoTipModal(false)}
            open={showInfoTipModal}
            title={infoTipModal?.title}
            content={infoTipModal?.content}
          />
        </>
      }

      <ApiErrorModal />

    </FlowScreen>
  );
}

export default Allocations;